import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

export type LoraChartStats = {
  current?: number
  average?: number
  min?: number
  max?: number
}

export type LoraSummaryCardsProps = {
  stats: LoraChartStats
  isLoading: boolean
}

const CARD_CONFIG = [
  { id: 'current', labelId: 'LORA.CURRENT', color: '#008FFB' },
  { id: 'average', labelId: 'LORA.AVERAGE', color: '#00E396' },
  { id: 'min', labelId: 'LORA.MIN', color: '#775DD0' },
  { id: 'max', labelId: 'LORA.MAX', color: '#FEB019' },
] as const

function formatValue(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function statValue(
  stats: LoraChartStats,
  id: (typeof CARD_CONFIG)[number]['id']
): number | undefined {
  return stats[id]
}

const LoraSummaryCards: React.FC<LoraSummaryCardsProps> = ({ stats, isLoading }) => {
  const intl = useIntl()

  const cards = useMemo(
    () =>
      CARD_CONFIG.map((cfg) => ({
        ...cfg,
        title: intl.formatMessage({ id: cfg.labelId }),
        value: statValue(stats, cfg.id),
      })),
    [stats, intl]
  )

  if (isLoading) {
    return (
      <div className='row g-4 g-xl-5 mb-5'>
        {[1, 2, 3, 4].map((key) => (
          <div className='col-6 col-md-3' key={key}>
            <div className='card border-0 h-100 placeholder-glow'>
              <div className='card-body py-5'>
                <span className='placeholder col-7 mb-3'></span>
                <span className='placeholder col-10 fs-2'></span>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='row g-4 g-xl-5 mb-5'>
      {cards.map((card) => (
        <div className='col-6 col-md-3' key={card.id}>
          <div
            className='card border-0 h-100 overflow-hidden'
            style={{
              background: `linear-gradient(145deg, #ffffff 35%, ${card.color}14 100%)`,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
            }}
          >
            <div className='card-body d-flex flex-column py-5 px-5'>
              <div className='d-flex align-items-center gap-2 mb-3'>
                <span
                  className='rounded-circle flex-shrink-0'
                  style={{ width: 10, height: 10, backgroundColor: card.color }}
                />
                <span className='fs-7 fw-bold text-gray-600 text-truncate'>{card.title}</span>
              </div>
              <div className='d-flex align-items-baseline gap-2'>
                <span className='fs-2x fw-bold text-gray-900'>{formatValue(card.value)}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default LoraSummaryCards
